/**
 * GRIDHEIMR — Bot Card Component
 *
 * Displays individual bot information with status and actions.
 * Running bots pulse green; error bots glow red; status badges in pixel font.
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Bot } from "../api/types";
import { BotStatus, BotType } from "../api/types";

import { BLUE, YELLOW, PIXEL, MONO, ensureFonts } from "../theme";

/**
 * Component props
 */
interface BotCardProps {
  bot: Bot;
  onStart?: (botId: string) => void;
  onStop?: (botId: string) => void;
  onRestart?: (botId: string) => void;
  onDelete?: (botId: string) => void;
}

function getStatusDisplay(status: BotStatus): { color: string; label: string } {
  switch (status) {
    case BotStatus.RUNNING:   return { color: "#00c97a", label: "ONLINE"    };
    case BotStatus.STOPPED:   return { color: "rgba(255,255,255,0.3)", label: "OFFLINE"   };
    case BotStatus.STARTING:  return { color: BLUE,      label: "STARTING"  };
    case BotStatus.STOPPING:  return { color: YELLOW,    label: "STOPPING"  };
    case BotStatus.CREATING:  return { color: BLUE,      label: "CREATING"  };
    case BotStatus.FAILED:    return { color: "#ef4444", label: "ERROR"     };
    case BotStatus.DESTROYED: return { color: "rgba(255,255,255,0.2)", label: "DESTROYED" };
    default:                  return { color: "rgba(255,255,255,0.3)", label: status      };
  }
}

function getCardClass(status: BotStatus): string {
  if (status === BotStatus.RUNNING) return "gh-bot-card gh-bot-card-running";
  if (status === BotStatus.FAILED)  return "gh-bot-card gh-bot-card-error";
  return "gh-bot-card";
}

/**
 * Get bot type display icon
 */
function getBotTypeIcon(botType: BotType): string {
  switch (botType) {
    case BotType.MOLTBOT:
      return "\u{1F916}";
    case BotType.OPENCLAW:
      return "\u{1F99E}";
    case BotType.PICOCLAW:
      return "\u{1F980}";
    case BotType.PICOBOT:
      return "\u{1F41E}";
    case BotType.NANOBOT:
      return "\u{1F9EC}";
    case BotType.CUSTOM:
      return "\u{1F527}";
    default:
      return "\u{1F916}";
  }
}

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp?: string): string {
  if (!timestamp) return "Never";

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Bot Card Component
 *
 * Displays bot information including status, type, resources, and actions.
 */
export function BotCard({ bot, onStart, onStop, onRestart, onDelete }: BotCardProps) {
  const navigate = useNavigate();
  const statusDisplay = getStatusDisplay(bot.status);
  const botTypeIcon = getBotTypeIcon(bot.bot_type);

  const canStart = bot.status === BotStatus.STOPPED;
  const canStop = bot.status === BotStatus.RUNNING;
  const canRestart = bot.status === BotStatus.RUNNING;
  const canDelete = bot.status === BotStatus.STOPPED || bot.status === BotStatus.FAILED;

  useEffect(() => {
    ensureFonts("bot-card-fonts");
    if (!document.getElementById("bot-card-css")) {
      const s = Object.assign(document.createElement("style"), { id: "bot-card-css" });
      s.textContent = `
        @keyframes pulse-green {
          0%, 100% {
            box-shadow: 0 0 6px rgba(0,201,122,0.25), 0 0 12px rgba(0,201,122,0.12);
            border-color: rgba(0,201,122,0.4);
          }
          50% {
            box-shadow: 0 0 18px rgba(0,201,122,0.55), 0 0 36px rgba(0,201,122,0.25);
            border-color: rgba(0,201,122,0.9);
          }
        }
        .gh-bot-card { transition: box-shadow 0.3s, border-color 0.3s; cursor: pointer; }
        .gh-bot-card:hover {
          border-color: ${BLUE} !important;
          box-shadow: 0 0 20px rgba(0,106,167,0.3) !important;
        }
        .gh-bot-card-running { animation: pulse-green 2.5s ease-in-out infinite; }
        .gh-bot-card-error {
          border-color: rgba(239,68,68,0.55) !important;
          box-shadow: 0 0 14px rgba(239,68,68,0.35), 0 0 28px rgba(239,68,68,0.15) !important;
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const handleCardClick = () => navigate(`/bots/${bot.id}`);

  return (
    <div
      className={getCardClass(bot.status)}
      style={styles.card}
      onClick={handleCardClick}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.typeIcon}>{botTypeIcon}</span>
          <div>
            <h3 style={styles.botName}>{bot.name}</h3>
            <p style={styles.botType}>{bot.bot_type}</p>
          </div>
        </div>
        {/* Pixel-font status badge */}
        <div style={{ ...styles.statusBadge, borderColor: statusDisplay.color, color: statusDisplay.color }}>
          <span style={styles.statusLabel}>{statusDisplay.label}</span>
        </div>
      </div>

      {/* Error Message */}
      {bot.error_message && (
        <div style={styles.errorBanner}>
          <span style={styles.errorIcon}>⚠️</span>
          <span style={styles.errorText}>{bot.error_message}</span>
        </div>
      )}

      {/* Info Grid */}
      <div style={styles.infoGrid}>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>vCPUs</span>
          <span style={styles.infoValue}>{bot.resources.vcpus}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Memory</span>
          <span style={styles.infoValue}>{bot.resources.memory_mb} MB</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Skills</span>
          <span style={styles.infoValue}>{bot.skills.length}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoLabel}>Last Active</span>
          <span style={styles.infoValue}>{formatRelativeTime(bot.last_active_at)}</span>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        {canStart && (
          <button
            style={{ ...styles.actionButton, ...styles.startButton }}
            onClick={(e) => { e.stopPropagation(); onStart?.(bot.id); }}>
            ▶ START
          </button>
        )}
        {canStop && (
          <button
            style={{ ...styles.actionButton, ...styles.stopButton }}
            onClick={(e) => { e.stopPropagation(); onStop?.(bot.id); }}>
            ■ STOP
          </button>
        )}
        {canRestart && (
          <button
            style={{ ...styles.actionButton, ...styles.restartButton }}
            onClick={(e) => { e.stopPropagation(); onRestart?.(bot.id); }}>
            ↺ RESTART
          </button>
        )}
        {canDelete && (
          <button
            style={{ ...styles.actionButton, ...styles.deleteButton }}
            onClick={(e) => { e.stopPropagation(); onDelete?.(bot.id); }}>
            ✕ DELETE
          </button>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: "#000a12",
    border: `1px solid rgba(0,106,167,0.2)`,
    padding: "1.5rem",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1rem",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    minWidth: 0,
  },
  typeIcon: { fontSize: "1.6rem", flexShrink: 0 },
  botName: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "1rem",
    fontWeight: 700,
    color: "#fff",
    letterSpacing: "0.04em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  botType: {
    margin: "0.2rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.35)",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    border: "1px solid",
    flexShrink: 0,
  },
  statusLabel: {
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    letterSpacing: "0.08em",
    lineHeight: 1.4,
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
    padding: "0.6rem 0.75rem",
    marginBottom: "1rem",
  },
  errorIcon: { fontSize: "0.9rem" },
  errorText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "#fca5a5",
    letterSpacing: "0.03em",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "0.75rem 1rem",
    marginBottom: "1.25rem",
    borderTop: `1px solid rgba(0,106,167,0.1)`,
    paddingTop: "1rem",
  },
  infoItem: { display: "flex", flexDirection: "column", gap: "0.2rem" },
  infoLabel: {
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    color: YELLOW,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    opacity: 0.7,
  },
  infoValue: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.75)",
    letterSpacing: "0.04em",
  },
  actions: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    borderTop: `1px solid rgba(0,106,167,0.1)`,
    paddingTop: "1rem",
  },
  actionButton: {
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    padding: "10px 16px",
    border: "1px solid",
    background: "transparent",
    cursor: "pointer",
    letterSpacing: "0.08em",
    transition: "all 0.2s ease",
  },
  startButton:   { borderColor: "#00c97a", color: "#00c97a" },
  stopButton:    { borderColor: YELLOW,    color: YELLOW    },
  restartButton: { borderColor: BLUE,      color: BLUE      },
  deleteButton:  { borderColor: "#ef4444", color: "#ef4444" },
};
